import type { Block } from './types'
import { blockText, normalizeSpace } from './text'

// "Share your writing": a pre-designed image of a published writing (title,
// author, a line from it, Burrow's mark) that the author downloads, copies or
// sends to an app. Drawn on a canvas, so the preview IS the exported file
// (the thinker quiz's share card works the same way).
//
// Built to be re-skinned. A template is a palette plus a layout:
//   - SHARE_FORMATS: the export sizes (add or drop one here, nothing else).
//   - SHARE_TEMPLATES: the designs. Each names its palette and fonts, and a
//     draw() that lays the content out with the helpers below. A new variant
//     is a new entry; the sheet lists whatever is here.
//   - ShareCardContent: everything a template may use.

export interface ShareCardContent {
  title: string
  subtitle: string
  author: string
  /** One line from the writing (see shareExcerpt). Empty when there's none worth quoting. */
  excerpt: string
  /** Where readers go: shown small, as the card's footer. */
  url: string
  minutes: number
}

export type ShareFormatId = 'square' | 'portrait' | 'story'

export interface ShareFormat {
  id: ShareFormatId
  label: string
  hint: string
  width: number
  height: number
}

export const SHARE_FORMATS: ShareFormat[] = [
  { id: 'square', label: 'Square', hint: '1:1 · posts', width: 1080, height: 1080 },
  { id: 'portrait', label: 'Portrait', hint: '4:5 · feeds', width: 1080, height: 1350 },
  { id: 'story', label: 'Story', hint: '9:16 · stories', width: 1080, height: 1920 },
]

export interface SharePalette {
  background: string
  ink: string
  soft: string
  accent: string
}

export interface ShareFonts {
  display: string
  serif: string
  sans: string
}

/** What a template's draw() gets: the canvas, the content, the size, and the helpers. */
export interface DrawArgs {
  ctx: CanvasRenderingContext2D
  content: ShareCardContent
  format: ShareFormat
  palette: SharePalette
  fonts: ShareFonts
  /** Burrow's bunny, ready to tint (null if it didn't load). */
  bunny: HTMLImageElement | null
}

export interface ShareTemplate {
  id: string
  label: string
  palette: SharePalette
  fonts?: Partial<ShareFonts>
  draw(args: DrawArgs): void
}

/** The app's faces (index.css --font-display / --font-serif / --font-sans). */
export const SHARE_FONTS: ShareFonts = {
  display: "'Fraunces', Georgia, serif",
  serif: "'Newsreader', Georgia, serif",
  sans: "'Manrope', system-ui, sans-serif",
}

/* ------------------------------------------------------------ the content */

const EXCERPT_MAX = 220

/**
 * The line a card quotes: the writing's first pull quote, else its first
 * embedded thought, else the opening of its first paragraph. Trimmed at a
 * word to fit the card.
 */
export function shareExcerpt(blocks: readonly Block[]): string {
  const pick =
    blocks.find((b) => b.type === 'pullquote') ??
    blocks.find((b) => b.type === 'thought') ??
    blocks.find((b) => b.type === 'text' && normalizeSpace(blockText(b)).length > 40)
  if (!pick) return ''
  const text = normalizeSpace(blockText(pick))
  if (text.length <= EXCERPT_MAX) return text
  const cut = text.slice(0, EXCERPT_MAX)
  const atWord = cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:.–—-]+$/, '')
  return `${atWord}…`
}

/** "can-ai-learn-to-have-taste-portrait.png" */
export function shareFileName(title: string, format: ShareFormatId): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'writing'
  return `${slug}-${format}.png`
}

/* ------------------------------------------------------------ the helpers */

/** Break `text` into lines no wider than `maxWidth`, at most `maxLines` (the last ends in "…"). */
export function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let line = ''
  for (let i = 0; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i]
    if (ctx.measureText(next).width <= maxWidth || !line) {
      line = next
      continue
    }
    lines.push(line)
    line = words[i]
    if (lines.length === maxLines) {
      const last = lines[maxLines - 1]
      lines[maxLines - 1] = fitEllipsis(ctx, last, maxWidth)
      return lines
    }
  }
  if (line) lines.push(line)
  if (lines.length > maxLines) {
    lines.length = maxLines
    lines[maxLines - 1] = fitEllipsis(ctx, lines[maxLines - 1], maxWidth)
  }
  return lines
}

function fitEllipsis(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  let t = text.replace(/[\s,;:.–—-]+$/, '')
  while (t && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, t.lastIndexOf(' ') > 0 ? t.lastIndexOf(' ') : t.length - 1)
  return `${t}…`
}

/** Draw lines top-down from y; returns the y under the last line. */
export function drawLines(ctx: CanvasRenderingContext2D, lines: string[], x: number, y: number, lineHeight: number): number {
  ctx.textBaseline = 'top'
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight))
  return y + lines.length * lineHeight
}

/** A row of cross-stitches (the footer's, the bunny's own stitch) across the width at y. */
export function drawStitches(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number, color: string, size = 14) {
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = 2.6
  ctx.lineCap = 'round'
  const step = size * 1.6
  for (let x = x0; x + size <= x1; x += step) {
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + size, y + size * 0.7)
    ctx.moveTo(x + size, y)
    ctx.lineTo(x, y + size * 0.7)
    ctx.stroke()
  }
  ctx.restore()
}

/** The bunny, tinted `color`, `width` wide with its top-left at (x, y). */
export function drawBunny(ctx: CanvasRenderingContext2D, bunny: HTMLImageElement | null, x: number, y: number, width: number, color: string) {
  if (!bunny || !bunny.naturalWidth) return
  const height = (width * bunny.naturalHeight) / bunny.naturalWidth
  const tint = document.createElement('canvas')
  tint.width = Math.round(width)
  tint.height = Math.round(height)
  const t = tint.getContext('2d')
  if (!t) return
  t.drawImage(bunny, 0, 0, tint.width, tint.height)
  t.globalCompositeOperation = 'source-in'
  t.fillStyle = color
  t.fillRect(0, 0, tint.width, tint.height)
  ctx.drawImage(tint, x, y)
}

/* ----------------------------------------------------------- the designs */

/** The shared layout: eyebrow, title, the line, then the author and the mark at the foot. */
function editorialLayout({ ctx, content, format, palette, fonts, bunny }: DrawArgs, opts: { stitches: boolean }) {
  const { width: W, height: H } = format
  const pad = Math.round(W * 0.09)
  const inner = W - pad * 2
  const tall = H / W
  const scale = tall > 1.5 ? 1.12 : 1

  ctx.fillStyle = palette.background
  ctx.fillRect(0, 0, W, H)
  if (opts.stitches) {
    drawStitches(ctx, pad, W - pad, pad * 0.55, palette.accent)
    drawStitches(ctx, pad, W - pad, H - pad * 0.55 - 10, palette.accent)
  }

  let y = pad + (tall > 1.5 ? H * 0.1 : tall > 1.1 ? H * 0.04 : 0)

  // Eyebrow
  ctx.fillStyle = palette.accent
  ctx.font = `600 ${Math.round(26 * scale)}px ${fonts.sans}`
  ctx.textBaseline = 'top'
  ctx.fillText('A NEW WRITING', pad, y)
  y += Math.round(64 * scale)

  // Title
  ctx.fillStyle = palette.ink
  const titleSize = Math.round((content.title.length > 60 ? 64 : 80) * scale)
  ctx.font = `400 ${titleSize}px ${fonts.display}`
  y = drawLines(ctx, wrapLines(ctx, content.title || 'Untitled', inner, 4), pad, y, Math.round(titleSize * 1.12))

  // Subtitle
  if (content.subtitle) {
    y += Math.round(18 * scale)
    ctx.fillStyle = palette.soft
    const subSize = Math.round(34 * scale)
    ctx.font = `italic 300 ${subSize}px ${fonts.serif}`
    y = drawLines(ctx, wrapLines(ctx, content.subtitle, inner, 2), pad, y, Math.round(subSize * 1.3))
  }

  // The line, with a rule beside it, in whatever room is left above the foot.
  const footTop = H - pad - Math.round(150 * scale)
  if (content.excerpt) {
    y += Math.round(56 * scale)
    const quoteSize = Math.round(38 * scale)
    const lh = Math.round(quoteSize * 1.42)
    const room = Math.max(0, Math.floor((footTop - 40 - y) / lh))
    if (room >= 2) {
      ctx.font = `italic 400 ${quoteSize}px ${fonts.serif}`
      const lines = wrapLines(ctx, `“${content.excerpt}”`, inner - 40, Math.min(room, tall > 1.5 ? 9 : 6))
      ctx.fillStyle = palette.accent
      ctx.fillRect(pad, y + 6, 5, lines.length * lh - 12)
      ctx.fillStyle = palette.ink
      drawLines(ctx, lines, pad + 40, y, lh)
    }
  }

  // Foot: author and read time on the left, the bunny and wordmark on the right.
  const footY = H - pad - Math.round(96 * scale)
  ctx.fillStyle = palette.ink
  ctx.font = `400 ${Math.round(38 * scale)}px ${fonts.display}`
  ctx.textBaseline = 'top'
  ctx.fillText(content.author, pad, footY, inner * 0.6)
  ctx.fillStyle = palette.soft
  ctx.font = `500 ${Math.round(24 * scale)}px ${fonts.sans}`
  ctx.fillText(`${content.minutes} min read · ${content.url}`, pad, footY + Math.round(54 * scale), inner * 0.7)

  const bunnyW = Math.round(76 * scale)
  drawBunny(ctx, bunny, W - pad - bunnyW, footY - Math.round(6 * scale), bunnyW, palette.ink)
  ctx.fillStyle = palette.ink
  ctx.font = `italic 500 ${Math.round(30 * scale)}px ${fonts.display}`
  ctx.textAlign = 'right'
  ctx.fillText('Burrow', W - pad, footY + Math.round(64 * scale))
  ctx.textAlign = 'left'
}

export const SHARE_TEMPLATES: ShareTemplate[] = [
  {
    id: 'paper',
    label: 'Paper',
    palette: { background: '#F7F1EB', ink: '#2D1B1E', soft: '#8A7668', accent: '#C2646D' },
    draw: (args) => editorialLayout(args, { stitches: false }),
  },
  {
    id: 'wine',
    label: 'Wine',
    palette: { background: '#4A1525', ink: '#FDF8F3', soft: 'rgba(253,248,243,0.66)', accent: '#D4848B' },
    draw: (args) => editorialLayout(args, { stitches: true }),
  },
  {
    id: 'night',
    label: 'Night',
    palette: { background: '#1A1210', ink: '#E8DDD1', soft: '#9A8A7C', accent: '#D4848B' },
    draw: (args) => editorialLayout(args, { stitches: false }),
  },
]

/* ------------------------------------------------------------- rendering */

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (typeof Image === 'undefined') return resolve(null)
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

/** Draw the card. Null where there's no canvas (old browsers, tests). */
export async function renderShareCard(
  content: ShareCardContent,
  template: ShareTemplate,
  format: ShareFormat,
  bunnySrc: string | null,
): Promise<HTMLCanvasElement | null> {
  const canvas = document.createElement('canvas')
  canvas.width = format.width
  canvas.height = format.height
  let ctx: CanvasRenderingContext2D | null = null
  try {
    ctx = canvas.getContext('2d')
  } catch {
    ctx = null
  }
  if (!ctx) return null
  try {
    // The web fonts must be in before the canvas uses them, or it falls back to Georgia.
    await document.fonts?.ready
  } catch {
    /* draw with what there is */
  }
  const bunny = bunnySrc ? await loadImage(bunnySrc) : null
  template.draw({ ctx, content, format, palette: template.palette, fonts: { ...SHARE_FONTS, ...template.fonts }, bunny })
  return canvas
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png')
    } catch {
      resolve(null)
    }
  })
}
